import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Clock, Phone, Coffee, RefreshCcw, Bell, BellOff, Gift } from "lucide-react";
import { buildAdminApi, getAdminPin } from "../lib/admin";
import { toast } from "sonner";
import { customLabel } from "../lib/session";
import FirstPaymentCelebration from "../components/FirstPaymentCelebration";

const tabs = [
    { key: "active", label: "Active" },
    { key: "completed", label: "Completed" },
];

const Staff = () => {
    const { slug } = useParams();
    const nav = useNavigate();
    const ctx = useOutletContext() || {};
    const cafeName = ctx.tenant?.name || "Coffeein7";
    const api = useMemo(() => buildAdminApi(slug), [slug]);

    useEffect(() => {
        if (!getAdminPin(slug)) {
            toast.error("Staff sign-in required");
            nav(`/${slug}/admin`);
        }
    }, [slug, nav]);

    const [orders, setOrders] = useState([]);
    const [redemptions, setRedemptions] = useState([]);
    const [tab, setTab] = useState("active");
    const [loading, setLoading] = useState(false);
    const [lastFetch, setLastFetch] = useState(null);
    const [soundOn, setSoundOn] = useState(true);
    const [newIds, setNewIds] = useState(new Set());
    const seenOrderIds = useRef(new Set());
    const isFirstLoad = useRef(true);

    const playChime = () => {
        try {
            const AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            const ctx = new AC();
            const now = ctx.currentTime;
            [880, 1320].forEach((freq, i) => {
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.type = "sine";
                o.frequency.value = freq;
                g.gain.setValueAtTime(0, now + i * 0.14);
                g.gain.linearRampToValueAtTime(0.18, now + i * 0.14 + 0.02);
                g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.14 + 0.35);
                o.connect(g).connect(ctx.destination);
                o.start(now + i * 0.14);
                o.stop(now + i * 0.14 + 0.4);
            });
        } catch {
            /* ignore */
        }
    };

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get(`/staff/orders`, { params: { status: tab } });
            const list = r.data.orders;
            setOrders(list);
            setLastFetch(new Date());

            if (tab === "active") {
                const currentIds = new Set(list.map((o) => o.id));
                if (isFirstLoad.current) {
                    seenOrderIds.current = currentIds;
                    isFirstLoad.current = false;
                } else {
                    const brandNew = list.filter((o) => !seenOrderIds.current.has(o.id));
                    if (brandNew.length > 0) {
                        if (soundOn) playChime();
                        toast.success(
                            brandNew.length === 1
                                ? `New order ${brandNew[0].order_number}`
                                : `${brandNew.length} new orders`,
                            { duration: 4000 },
                        );
                        setNewIds((prev) => {
                            const next = new Set(prev);
                            brandNew.forEach((o) => next.add(o.id));
                            return next;
                        });
                        setTimeout(() => {
                            setNewIds((prev) => {
                                const next = new Set(prev);
                                brandNew.forEach((o) => next.delete(o.id));
                                return next;
                            });
                        }, 8000);
                    }
                    seenOrderIds.current = currentIds;
                }
            }
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    }, [tab, soundOn]);

    useEffect(() => {
        isFirstLoad.current = true;
        seenOrderIds.current = new Set();
        fetchOrders();
        const int = setInterval(fetchOrders, 5000);
        return () => clearInterval(int);
    }, [fetchOrders]);

    const fetchRedemptions = useCallback(async () => {
        try {
            const r = await api.get("/staff/redemptions");
            setRedemptions(r.data.redemptions || []);
        } catch {
            /* silent */
        }
    }, []);

    useEffect(() => {
        fetchRedemptions();
        const int = setInterval(fetchRedemptions, 5000);
        return () => clearInterval(int);
    }, [fetchRedemptions]);

    const markRedemptionComplete = async (id) => {
        try {
            await api.patch(`/staff/redemptions/${id}/status`, { status: "completed" });
            toast.success("Redemption handed over");
            fetchRedemptions();
        } catch {
            toast.error("Update failed");
        }
    };

    const setStatus = async (id, status) => {
        try {
            await api.patch(`/staff/orders/${id}/status`, { status });
            toast.success(status === "ready" ? "Marked ready" : "Marked completed");
            fetchOrders();
        } catch {
            toast.error("Update failed");
        }
    };

    const activeCount = orders.filter((o) => o.status !== "completed").length;

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <FirstPaymentCelebration api={api} surface="staff" cafeName={cafeName} />
            <div className="max-w-7xl mx-auto px-6 py-8">
                {/* Header */}
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-[#8C7A72] font-semibold">
                            Staff dashboard
                        </div>
                        <h1 className="font-display font-black text-4xl mt-1">
                            {cafeName} · Kitchen
                        </h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="text-xs text-[#8C7A72]">
                            {lastFetch
                                ? `Updated ${lastFetch.toLocaleTimeString([], { minute: "2-digit", second: "2-digit" })}`
                                : ""}
                        </div>
                        <button
                            data-testid="sound-toggle"
                            onClick={() => setSoundOn(!soundOn)}
                            title={soundOn ? "Sound on" : "Sound off"}
                            className={`h-10 w-10 rounded-full border flex items-center justify-center active:scale-95 ${
                                soundOn
                                    ? "bg-[#FF4F18] text-white border-[#FF4F18]"
                                    : "bg-white text-[#8C7A72] border-[#E8DCCB]"
                            }`}
                        >
                            {soundOn ? (
                                <Bell className="h-4 w-4" strokeWidth={2.5} />
                            ) : (
                                <BellOff className="h-4 w-4" strokeWidth={2.5} />
                            )}
                        </button>
                        <button
                            data-testid="refresh-btn"
                            onClick={fetchOrders}
                            className="h-10 w-10 rounded-full bg-white border border-[#E8DCCB] flex items-center justify-center active:scale-95"
                        >
                            <RefreshCcw
                                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                                strokeWidth={2.5}
                            />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mt-8 flex gap-2 border-b border-[#E8DCCB]">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            data-testid={`tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`px-6 py-3 font-display font-bold text-sm relative ${
                                tab === t.key ? "text-[#FF4F18]" : "text-[#8C7A72]"
                            }`}
                        >
                            {t.label}
                            {tab === t.key && (
                                <motion.div
                                    layoutId="tab-underline"
                                    className="absolute bottom-0 left-0 right-0 h-1 bg-[#FF4F18] rounded-t-full"
                                />
                            )}
                        </button>
                    ))}
                    <div className="ml-auto self-center text-sm text-[#8C7A72] pr-2">
                        {activeCount} active
                    </div>
                </div>

                {/* Redemption tickets */}
                {redemptions.length > 0 && (
                    <div className="mt-8" data-testid="staff-redemptions">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72] mb-3">
                            <Gift className="h-3.5 w-3.5 text-[#FF4F18]" strokeWidth={2.5} />
                            Redemptions to hand over · {redemptions.length}
                        </div>
                        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            {redemptions.map((r) => (
                                <div
                                    key={r.id}
                                    data-testid={`redemption-${r.code}`}
                                    className="bg-white rounded-2xl p-4 border border-[#FF4F18]/30 border-l-4 border-l-[#FF4F18] flex flex-col gap-2"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="text-3xl shrink-0">{r.item_emoji || "🎁"}</div>
                                            <div className="min-w-0">
                                                <div className="font-display font-black text-sm truncate">
                                                    {r.item_name}
                                                </div>
                                                <div className="text-[10px] text-[#8C7A72] truncate">
                                                    {r.customer_name || "Customer"} · ···{r.phone_last4}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="font-mono font-black text-sm text-[#FF4F18] shrink-0">
                                            {r.code}
                                        </div>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C7A72] font-bold">
                                        🫘 {r.cost_beans} beans
                                    </div>
                                    <button
                                        data-testid={`redemption-complete-${r.code}`}
                                        onClick={() => markRedemptionComplete(r.id)}
                                        className="mt-1 w-full py-2 rounded-full bg-[#1B995E] text-white font-display font-bold text-xs active:scale-95 flex items-center justify-center gap-1"
                                    >
                                        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                                        Handed over
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Grid */}
                <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    <AnimatePresence>
                        {orders.map((o) => (
                            <OrderCard
                                key={o.id}
                                order={o}
                                onStatus={setStatus}
                                isNew={newIds.has(o.id)}
                            />
                        ))}
                    </AnimatePresence>
                </div>
                {orders.length === 0 && !loading && (
                    <div className="mt-16 text-center text-[#8C7A72]">
                        <Coffee className="h-10 w-10 mx-auto mb-4" strokeWidth={2} />
                        <div className="font-display font-bold text-xl">All caught up</div>
                        <div className="text-sm">No {tab} orders right now.</div>
                    </div>
                )}
            </div>
        </div>
    );
};

const OrderCard = ({ order, onStatus, isNew }) => {
    const ready = new Date(order.pickup_ready_at);
    const timeStr = ready.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const minsAway = Math.max(0, Math.round((ready.getTime() - Date.now()) / 60000));

    const statusColor =
        order.status === "ready"
            ? "border-l-[#1B995E]"
            : order.status === "completed"
            ? "border-l-[#8C7A72]"
            : "border-l-[#FF4F18]";

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{
                opacity: 1,
                y: 0,
                boxShadow: isNew
                    ? [
                          "0 0 0 0 rgba(255,79,24,0)",
                          "0 0 0 8px rgba(255,79,24,0.35)",
                          "0 0 0 0 rgba(255,79,24,0)",
                      ]
                    : "0 4px 20px rgba(44,26,18,0.04)",
            }}
            transition={{
                boxShadow: isNew
                    ? { duration: 1.4, repeat: 5, ease: "easeInOut" }
                    : { duration: 0.3 },
            }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`bg-white rounded-3xl p-5 border border-[#E8DCCB] border-l-4 ${statusColor} flex flex-col gap-3 relative`}
            data-testid={`staff-order-${order.order_number}`}
        >
            {isNew && (
                <div
                    className="absolute -top-2 right-4 bg-[#FF4F18] text-white text-[10px] uppercase tracking-[0.2em] font-black px-2.5 py-1 rounded-full"
                    data-testid={`new-badge-${order.order_number}`}
                >
                    New
                </div>
            )}
            <div className="flex items-center justify-between">
                <div>
                    <div className="font-display font-black text-xl">{order.order_number}</div>
                    {order.customer_name && (
                        <div className="mt-1 inline-flex items-center gap-1 bg-[#2C1A12] text-white rounded-full px-2 py-0.5" data-testid={`staff-name-${order.order_number}`}>
                            <span className="text-[9px] uppercase tracking-[0.2em] font-bold opacity-60">for</span>
                            <span className="font-display font-black text-sm">
                                {order.customer_name}
                            </span>
                        </div>
                    )}
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#8C7A72] font-bold flex items-center gap-1">
                    <Phone className="h-3 w-3" strokeWidth={2.5} />
                    ···{order.phone_last4}
                </div>
            </div>

            <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-[#FF4F18]" strokeWidth={2.5} />
                <span className="font-display font-bold">{timeStr}</span>
                <span className="text-[#8C7A72]">
                    · {minsAway > 0 ? `${minsAway} min` : "now"}
                </span>
                {order.payment_method === "counter" && (
                    <span className="ml-auto text-[10px] uppercase tracking-[0.15em] bg-[#2C1A12] text-white px-2 py-0.5 rounded-full font-bold">
                        Counter pay
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-2 border-t border-[#E8DCCB]/60 pt-3">
                {order.items.map((i, idx) => {
                    const c = i.customizations ? customLabel(i.customizations) : "";
                    return (
                        <div key={idx} className="text-sm">
                            <div className="flex justify-between">
                                <span className="font-semibold">
                                    {i.qty}× {i.name}
                                    {i.size ? ` (${i.size === "L" ? "Lg" : "Sm"})` : ""}
                                </span>
                                <span className="text-[#8C7A72]">
                                    ${(i.qty * i.price).toFixed(2)}
                                </span>
                            </div>
                            {c && (
                                <div className="text-[11px] font-semibold text-[#FF4F18] mt-0.5">
                                    → {c}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center justify-between border-t border-[#E8DCCB]/60 pt-3">
                <span className="text-xs uppercase tracking-[0.2em] text-[#8C7A72] font-bold">
                    Total
                </span>
                <span className="font-display font-black text-lg">${order.total.toFixed(2)}</span>
            </div>

            <div className="flex gap-2 mt-1">
                {order.status === "pending" && (
                    <button
                        data-testid={`mark-ready-${order.order_number}`}
                        onClick={() => onStatus(order.id, "ready")}
                        className="flex-1 py-3 rounded-full bg-[#FF4F18] text-white font-display font-bold text-sm active:scale-95"
                    >
                        Mark ready
                    </button>
                )}
                {order.status === "ready" && (
                    <button
                        data-testid={`mark-completed-${order.order_number}`}
                        onClick={() => onStatus(order.id, "completed")}
                        className="flex-1 py-3 rounded-full bg-[#1B995E] text-white font-display font-bold text-sm active:scale-95 flex items-center justify-center gap-1.5"
                    >
                        <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                        Picked up
                    </button>
                )}
                {order.status === "completed" && (
                    <div className="flex-1 py-3 rounded-full bg-[#F9F6F0] text-[#8C7A72] font-display font-bold text-sm text-center">
                        Completed
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default Staff;
